import { getFullNameFromUser, notNull } from "@gap/utils/utils"
import { apiClient } from "api/trpc/trpc-client"
import { postgresApiClient } from "api/trpc-pg/trpc-client"
import { GapAutocompleteOption } from "features/gap-components/autocomplete/types/GapAutocompleteTypes"
import Loader from "reusable/mobx-helpers/Loader"
import { formHandler, FormHandlerConfig } from "reusable/mobx-store-utils/form-handlers/new/form-handler"
import { createStoreContext } from "reusable/mobx-store-utils/storeUtils"
import { ShareDocumentDialogProps } from "./ShareDocumentDialog"
import OrgGuardStore from "features/main/main-router/org-guard/OrgGuardStore"
import { DocShareDto } from "@gap/backend/app-services/doc-share/models/DocShareDto"
import ShareAnalytics from "analytics/ShareAnalytics"
import { snackbarSuccess } from "reusable/mui/snackbar/snackbar"
import { AdvisorClientContactDto } from "@gap/backend/app-services/advisor/client/contact/models/AdvisorClientContactDto"
import { FYIDto } from "@gap/backend/app-services/fyi/models/FYIDto"
import { findClosestMatch } from "reusable/utils/array-utils"

export type ShareFromSource = 'Session finalisation' | 'Meeting deliverable'

interface ShareDocumentDialogStoreProps extends ShareDocumentDialogProps {
   orgGuardStore: OrgGuardStore
   source: ShareFromSource
}

export default class ShareDocumentDialogStore {
   sp
   loader = new Loader()
   fields = {
      selectedContactsField: [] as string[],
      selectedFYIClientId: undefined as string | undefined
   }
   usersBeingRemoved: string[] = []
   sendInviteLoader = new Loader()
   contacts: AdvisorClientContactDto.minimal[] = []
   documentAccessRights: DocShareDto.DocumentAccessRights[]
   activeMemberCountLoader = new Loader(true)
   activeMemberCount: number = 0
   clientIsPublicToAdvisorOrg = true
   advisorAccessUsers: { id: string, name: string, email: string }[] = []
   advisorAccessPopoverAnchorEl: HTMLElement | null = null

   constructor(sp: ShareDocumentDialogStoreProps) {
      this.sp = sp
      this.contacts = sp.client.contacts
      this.documentAccessRights = sp.documentAccessRights
   }

   get formHandler() {
      return formHandler(this.fields, {
         selectedContactsField: { type: 'any' },
         selectedFYIClientId: { type: 'string' },
      } satisfies FormHandlerConfig)
   }

   get documentData() {
      switch (this.sp.document.type) {
         case 'workspace-document':
            return notNull(this.documentAccessRights.find(({ document }) => document.type === 'workspace-document' && document.documentId === (this.sp.document as DocShareDto.WorkspaceDocument).documentId))
         case 'business-plan-document':
            return notNull(this.documentAccessRights.find(({ document }) => document.type === 'business-plan-document'))
         case 'proposal-document':
            return notNull(this.documentAccessRights.find(({ document }) => document.type === 'proposal-document'))
         case 'markdown-document':
            return notNull(this.documentAccessRights.find(({ document }) => document.type === 'markdown-document' && document.markdownId === (this.sp.document as DocShareDto.MarkdownDocument).markdownId))
      }
   }

   get primaryUserHasAccessByDefault() {
      if (this.documentData.document.type === 'markdown-document') return false
      return !(this.documentData.document.type === 'workspace-document' && (this.documentData.document.docTypeName === 'Pre-work' || this.documentData.document.docTypeName === 'Feedback'))
   }

   get selectableClientUsers() {
      return this.contacts.map(contact => {
         if (this.documentData.users.some(x => x.email === contact.email)) {
            return null
         }

         const name = contact.firstName ? (getFullNameFromUser(contact) || contact.name) : contact.name

         return {
            id: contact.id,
            label: `${name} (${contact.email})`,
         }
      }).filter(clientContact => !!clientContact) as GapAutocompleteOption[]
   }

   get primaryDialogButton() {
      return {
         name: 'Send invite',
         callback: this.sendInvite,
         loading: this.sendInviteLoader.isLoading,
         disabled: this.fields.selectedContactsField.length === 0 || this.whoHasAccessList.some(x => !x?.name)
      }
   }

   get whoHasAccessList() {
      const whoHasAccess: ({ name: string, email: string, isPrimaryContact?: boolean } | null)[] = []

      for (const user of this.documentData.users || []) {
         if (this.usersBeingRemoved.includes(user.email)) {
            // Push null to trigger SkeletonLoader
            whoHasAccess.push(null)
         } else {
            whoHasAccess.push({
               name: user.name,
               email: user.email,
               isPrimaryContact: user.isPrimaryContact
            })
         }
      }

      return whoHasAccess
   }

   get infoBoxText(): string | null {
      if (this.sp.document.type === 'markdown-document') return null
      if (this.sp.document.type !== 'workspace-document' || this.sp.document.docTypeName === 'Delivery') {
         return "These settings won't apply to this client's admin users, who have access to all documents by default"
      }

      return "Recipients can only view their own responses by default. Share this document to let someone view all responses."
   }

   get advisorAccessLabel(): string {
      const advisorOrgName = this.sp.orgGuardStore.org?.name
      return this.clientIsPublicToAdvisorOrg ? `Everyone at ${advisorOrgName}` : `Team members at ${advisorOrgName}`
   }

   get advisorAccessPopoverIsOpen(): boolean {
      return !!this.advisorAccessPopoverAnchorEl
   }

   openAdvisorAccessPopover = (event: React.MouseEvent<HTMLElement>) => {
      this.advisorAccessPopoverAnchorEl = event.currentTarget
   }

   closeAdvisorAccessPopover = () => {
      this.advisorAccessPopoverAnchorEl = null
   }

   updateContacts = (contacts: AdvisorClientContactDto.minimal[]) => {
      this.contacts = contacts
   }

   /**
    * Preselects the FYI client once: by XPM uuid as pages arrive, by closest name once every page is in.
    * @param params.clients FYI clients loaded so far
    * @param params.isLoadingClients Whether more pages are still loading
    */
   preselectFYIClient = (params: { clients: FYIDto.clientMinimal[] | undefined, isLoadingClients: boolean }) => {
      if (this.fields.selectedFYIClientId) {
         return
      }
      const clients = params.clients ?? []
      const uuidMatch = this.sp.client.xeroId ? clients.find(client => client.uuid === this.sp.client.xeroId) : undefined
      if (uuidMatch) {
         this.fields.selectedFYIClientId = `${uuidMatch.id}`
         return
      }
      if (params.isLoadingClients) {
         return
      }
      const nameMatch = findClosestMatch(this.sp.client.name, clients, 'name')
      this.fields.selectedFYIClientId = nameMatch ? `${nameMatch.id}` : ''
   }

   removeClientAccessButtonHandler = (event: React.MouseEvent<HTMLElement>) => {
      const email = event.currentTarget.getAttribute("data-email")
      if (email) {
         this.removeClientAccess(email)
      }
   }

   onContactIdsChange = (selectedContactList: string[], advisorContact?: AdvisorClientContactDto.entity) => {
      if (advisorContact && !this.contacts.some(x => x.id === advisorContact.id)) {
         this.contacts = [...this.contacts, {
            id: advisorContact.id,
            name: advisorContact.name,
            firstName: advisorContact.firstName,
            lastName: advisorContact.lastName,
            email: advisorContact.email,
            status: advisorContact.status,
         }]
      }
      this.fields.selectedContactsField = selectedContactList
   }

   sendInvite = async () => {
      await this.sendInviteLoader.executeLoading(async () => {
         const contactIds = [...this.fields.selectedContactsField]
         this.fields.selectedContactsField = []
         for (const contactId of contactIds) {
            const contact = notNull(this.contacts.find(contact => contact.id === contactId))
            this.documentData.users.push({
               email: contact.email,
               isPrimaryContact: false,
               name: contact.name,
               userId: 'unused'
            })
         }

         await postgresApiClient.docShare.grantDocumentAccessRights.mutate({
            clientOrgId: this.sp.client.orgId,
            document: this.documentData.document,
            contactIds,
            clientId: this.sp.client.advisorClientId
         })

         ShareAnalytics.documentShared({
            "Initiated from": this.sp.source,
            "Share action": 'Invite user'
         })

         this.sp.modalState.close()
         this.sp.onInviteSent?.()

         snackbarSuccess('Document shared')
      })
   }

   fetchClientAdvisorAccess = async () => {
      await this.activeMemberCountLoader.executeLoading(async () => {
         const { isPublicToAdvisorOrg, activeCount, users } = await apiClient.advisor.getClientAdvisorAccess.query({
            advisorClientId: this.sp.client.advisorClientId,
         })
         this.clientIsPublicToAdvisorOrg = isPublicToAdvisorOrg
         this.activeMemberCount = activeCount
         this.advisorAccessUsers = users
      })
   }

   removeClientAccess = async (email: string) => {
      const contact = notNull(this.contacts.find(x => x.email === email), 'Unable to find contact')

      if (this.fields.selectedContactsField.includes(contact.id)) {
         // User has been selected, and thus has no access rights yet. We just need to remove from the list
         this.fields.selectedContactsField = this.fields.selectedContactsField.filter(x => x !== contact.id)
      } else {
         // User is not selected, which means this user already has access to this document
         // Remove from list, triggering SkeletonLoader
         this.usersBeingRemoved.push(contact.email)

         // Do actual delete
         const user = notNull(this.documentData.users.find(x => x.email === contact.email))
         await apiClient.docShare.revokeDocumentAccessRights.mutate({
            clientOrgId: this.sp.client.orgId,
            document: this.documentData.document,
            userId: user.userId,
            email: contact.email
         })

         // Remove user from access list
         this.documentAccessRights = this.documentAccessRights.map(documentAccessRight => {
            if (documentAccessRight.document.type === this.sp.document.type) {
               documentAccessRight.users = documentAccessRight.users.filter(x => x.email !== contact.email)
            }
            return documentAccessRight
         })
         this.usersBeingRemoved = this.usersBeingRemoved.filter(x => x !== contact.email)
      }
   }
}

export const { Provider: ShareDocumentDialogStoreProvider, useProvidedStore: useShareDocumentDialogStore } = createStoreContext(ShareDocumentDialogStore)
